import React, { useState } from 'react';
import {
  CheckCircle,
  Edit3,
  MessageSquare,
  Package,
  RotateCcw,
  Send,
  ThumbsDown,
  ThumbsUp,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface ClauseProposalProps {
  clauseId: string;
  clauseHierarchicalId: string;
  title: string;
  description: string;
  originalValue: any;
  currentValue: any;
  isNegotiable: boolean;
  isRequired: boolean;
  validation?: {
    type?:
      | 'text'
      | 'number'
      | 'currency'
      | 'percentage'
      | 'date'
      | 'multiline'
      | 'boolean';
    options?: Array<string | { value: string; label: string }>;
    min?: number;
    max?: number;
  };
  userRole: 'buyer' | 'seller' | 'agent';
  onProposalCreated?: (
    clauseId: string,
    clauseHierarchicalId: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => void;
  onValueAccepted?: (clauseId: string, value: any) => void;
  onValueRejected?: (clauseId: string) => void;
  onAddToBatch?: (
    clauseId: string,
    clauseHierarchicalId: string,
    title: string,
    originalValue: any,
    proposedValue: any,
    message?: string
  ) => void;
  onTogglePriority?: (clauseId: string) => void; // NEW: For agent to mark priorities
  isPriority?: boolean; // NEW: To show priority indicator
  isSelected?: boolean;
  className?: string;
}

export const ClauseProposal: React.FC<ClauseProposalProps> = ({
  clauseId,
  clauseHierarchicalId,
  title,
  description,
  originalValue,
  currentValue,
  isNegotiable,
  isRequired,
  validation,
  userRole,
  onProposalCreated,
  onValueAccepted,
  onValueRejected,
  onAddToBatch,
  onTogglePriority,
  isPriority = false,
  isSelected = false,
  className,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [proposedValue, setProposedValue] = useState(currentValue);
  const [proposalMessage, setProposalMessage] = useState('');
  const [showProposalForm, setShowProposalForm] = useState(false);

  const handleAccept = () => {
    onValueAccepted?.(clauseId, currentValue);
    toast.success(`Accepted ${title}`);
  };

  const handleReject = () => {
    onValueRejected?.(clauseId);
    toast.error(`Rejected ${title}`);
  };

  const handlePropose = () => {
    if (proposedValue === currentValue) {
      toast.warning('Proposed value is the same as current value');
      return;
    }

    onProposalCreated?.(
      clauseId,
      clauseHierarchicalId,
      currentValue,
      proposedValue,
      proposalMessage
    );
    setShowProposalForm(false);
    setProposalMessage('');
    toast.success(`Proposal created for ${title}`);
  };

  const handleEdit = () => {
    setProposedValue(currentValue);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setProposedValue(currentValue);
  };

  const handleSaveEdit = () => {
    if (proposedValue === currentValue) {
      toast.warning('No changes made');
      return;
    }

    onProposalCreated?.(
      clauseId,
      clauseHierarchicalId,
      currentValue,
      proposedValue,
      proposalMessage
    );
    setIsEditing(false);
    setProposalMessage('');
    toast.success(`Proposal created for ${title}`);
  };

  const handleAddToBatch = () => {
    if (proposedValue === currentValue) {
      toast.warning('Please change the value before adding to batch');
      return;
    }

    onAddToBatch?.(
      clauseId,
      clauseHierarchicalId,
      title,
      currentValue,
      proposedValue,
      proposalMessage
    );
    setIsEditing(false);
    setProposalMessage('');
    toast.success(`Added ${title} to batch proposals`);
  };

  const renderValueInput = () => {
    const toYmd = (value: any): string => {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      return d.toISOString().split('T')[0];
    };

    // Explicit boolean type
    if (validation?.type === 'boolean') {
      return (
        <select
          value={proposedValue ? 'true' : 'false'}
          onChange={(e) => setProposedValue(e.target.value === 'true')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // If we have explicit options, always render a dropdown.
    if (validation?.options && Array.isArray(validation.options)) {
      return (
        <select
          value={proposedValue ?? ''}
          onChange={(e) => setProposedValue(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Select...</option>
          {validation.options.map((option) => {
            const normalized =
              typeof option === 'string'
                ? { value: option, label: option }
                : option;
            return (
              <option key={normalized.value} value={normalized.value}>
                {normalized.label}
              </option>
            );
          })}
        </select>
      );
    }

    // Special-case: allow suggesting multiple inspection companies (one per line)
    if (clauseId === 'inspection-company') {
      return (
        <Textarea
          value={proposedValue || ''}
          onChange={(e) => setProposedValue(e.target.value)}
          className="w-full"
          placeholder={
            'Enter an inspection company (or list multiple, one per line)\nExample:\nABC Inspections\nXYZ Home Inspectors'
          }
        />
      );
    }

    // If we have explicit type, honor it.
    if (validation?.type === 'date') {
      const dateValue = toYmd(proposedValue);
      return (
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => setProposedValue(e.target.value)}
          className="w-full"
        />
      );
    }

    if (
      validation?.type === 'number' ||
      validation?.type === 'currency' ||
      validation?.type === 'percentage'
    ) {
      return (
        <Input
          type="number"
          value={proposedValue ?? ''}
          onChange={(e) => setProposedValue(Number(e.target.value))}
          step={validation?.type === 'percentage' ? '0.01' : 'any'}
          min={validation?.min}
          max={validation?.max}
          className="w-full"
          placeholder="Enter new value"
        />
      );
    }

    // Boolean (Yes/No) - use select dropdown
    if (typeof currentValue === 'boolean') {
      return (
        <select
          value={proposedValue ? 'true' : 'false'}
          onChange={(e) => setProposedValue(e.target.value === 'true')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        >
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      );
    }

    // Number (currency, percentage, etc.)
    if (typeof currentValue === 'number') {
      return (
        <Input
          type="number"
          value={proposedValue}
          onChange={(e) => setProposedValue(Number(e.target.value))}
          step="any"
          className="w-full"
          placeholder="Enter new value"
        />
      );
    }

    // Date - detect date strings (strict patterns only; do NOT rely on Date.parse which is overly permissive)
    const isDate =
      typeof currentValue === 'string' &&
      (Boolean(currentValue.match(/^\d{4}-\d{2}-\d{2}/)) ||
        Boolean(currentValue.match(/^\d{1,2}\/\d{1,2}\/\d{4}/)) ||
        // ISO timestamp-ish
        Boolean(currentValue.match(/^\d{4}-\d{2}-\d{2}T/)));

    if (isDate) {
      // Convert to YYYY-MM-DD format for date input
      const dateValue = toYmd(proposedValue);
      return (
        <Input
          type="date"
          value={dateValue}
          onChange={(e) => setProposedValue(e.target.value)}
          className="w-full"
        />
      );
    }

    // Default to text input
    return (
      <Input
        type="text"
        value={proposedValue || ''}
        onChange={(e) => setProposedValue(e.target.value)}
        className="w-full"
        placeholder="Enter new value"
      />
    );
  };

  const isValueChanged = proposedValue !== currentValue;
  const isAgreeable = !isNegotiable || currentValue === originalValue;

  // Detect if this is a compound proposal that affects multiple fields
  const isCompoundProposal = [
    'seller-assistance-amount',
    'earnest-money-deposit',
  ].includes(clauseId);

  // Calculate breakdown for compound proposals
  const getProposalBreakdown = () => {
    if (!isCompoundProposal || typeof proposedValue !== 'object') return null;

    // For seller assistance amount (compound: buyer agent fee + additional credit)
    if (clauseId === 'seller-assistance-amount') {
      if (
        proposedValue?.linkedValues &&
        Array.isArray(proposedValue.linkedValues)
      ) {
        const buyerAgentComp = proposedValue.linkedValues.find(
          (v: any) => v.clauseId === 'buyer-agent-compensation'
        );
        const additionalCredit = proposedValue.linkedValues.find(
          (v: any) => v.clauseId === 'additional-seller-credit'
        );

        return {
          total: proposedValue.total || 0,
          buyerAgentCompensation: buyerAgentComp?.value || 0,
          additionalCredit: additionalCredit?.value || 0,
        };
      }
    }

    return null;
  };

  const breakdown = getProposalBreakdown();
  const isDarkUi = userRole === 'agent';

  return (
    <Card
      className={`${className} ${isSelected ? 'ring-2 ring-blue-500' : ''}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle
              className={`text-lg flex items-center gap-2 ${
                isDarkUi ? 'text-gray-100' : ''
              }`}
            >
              {title}
              {isPriority && (
                <Badge className="bg-yellow-500 text-black text-xs">
                  ⭐ Priority
                </Badge>
              )}
              {isSelected && (
                <Badge className="bg-blue-600 text-white text-xs">
                  <CheckCircle className="h-3 w-3 mr-1" />
                  In Batch
                </Badge>
              )}
              {isRequired && (
                <Badge variant="destructive" className="text-xs">
                  Required
                </Badge>
              )}
            </CardTitle>
            <p
              className={`text-sm mt-1 ${
                isDarkUi ? 'text-gray-300' : 'text-gray-600'
              }`}
            >
              {description}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {userRole === 'agent' && onTogglePriority && (
              <Button
                size="sm"
                variant={isPriority ? 'default' : 'outline'}
                onClick={() => onTogglePriority(clauseId)}
                className={
                  isPriority
                    ? 'bg-yellow-500 text-black hover:bg-yellow-600'
                    : 'border-yellow-500 text-yellow-600 hover:bg-yellow-50'
                }
                title={
                  isPriority ? 'Remove priority' : 'Mark as priority for seller'
                }
              >
                ⭐ {isPriority ? 'Priority' : 'Set Priority'}
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Current Value Display */}
        <div>
          <Label
            className={`text-sm font-medium ${isDarkUi ? 'text-gray-200' : ''}`}
          >
            Current Value
          </Label>
          <div
            className={`mt-1 p-2 rounded text-sm ${
              isDarkUi ? 'bg-black/20 text-gray-100' : 'bg-gray-100'
            }`}
          >
            {typeof currentValue === 'object'
              ? JSON.stringify(currentValue)
              : String(currentValue)}
          </div>
        </div>

        {/* Original Value Display */}
        {originalValue !== currentValue && (
          <div>
            <Label
              className={`text-sm font-medium ${
                isDarkUi ? 'text-gray-200' : ''
              }`}
            >
              Original Value
            </Label>
            <div
              className={`mt-1 p-2 rounded text-sm ${
                isDarkUi ? 'bg-blue-900/30 text-gray-100' : 'bg-blue-100'
              }`}
            >
              {typeof originalValue === 'object'
                ? JSON.stringify(originalValue)
                : String(originalValue)}
            </div>
          </div>
        )}

        {/* Editing Mode */}
        {isEditing && (
          <div className="space-y-3">
            <div>
              <Label
                className={`text-sm font-medium ${
                  isDarkUi ? 'text-gray-200' : ''
                }`}
              >
                Proposed Value
              </Label>
              <div className="mt-1">{renderValueInput()}</div>
            </div>

            {/* Compound Proposal Breakdown */}
            {breakdown && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  This will update multiple fields:
                </p>
                <ul className="text-sm space-y-1">
                  <li className="text-blue-800">
                    • Buyer Agent Compensation: $
                    {breakdown.buyerAgentCompensation?.toLocaleString()}
                  </li>
                  <li className="text-blue-800">
                    • Additional Seller Credit: $
                    {breakdown.additionalCredit?.toLocaleString()}
                  </li>
                  <li className="font-bold text-blue-900 mt-2 pt-2 border-t border-blue-200">
                    Total Seller Assistance: $
                    {breakdown.total?.toLocaleString()}
                  </li>
                </ul>
              </div>
            )}

            <div>
              <Label className="text-sm font-medium">Message (Optional)</Label>
              <Textarea
                value={proposalMessage}
                onChange={(e) => setProposalMessage(e.target.value)}
                placeholder="Explain your proposed change..."
                rows={2}
                className="mt-1"
              />
            </div>

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={!isValueChanged}
              >
                <Send className="h-4 w-4 mr-2" />
                Submit Now
              </Button>
              {onAddToBatch && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleAddToBatch}
                  disabled={!isValueChanged}
                  className={isSelected ? 'bg-blue-100 border-blue-400' : ''}
                >
                  <Package className="h-4 w-4 mr-2" />
                  {isSelected ? 'Added to Batch' : 'Add to Batch'}
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!isEditing && (
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={handleAccept}
              className="bg-green-600 hover:bg-green-700"
            >
              <ThumbsUp className="h-4 w-4 mr-2" />
              Mark as Agreeable
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleEdit}
              className={isSelected ? 'border-blue-500 bg-blue-50' : ''}
            >
              <Edit3 className="h-4 w-4 mr-2" />
              {isSelected ? 'Edit Proposal' : 'Propose Change'}
            </Button>
            {!isAgreeable && (
              <Button size="sm" variant="destructive" onClick={handleReject}>
                <ThumbsDown className="h-4 w-4 mr-2" />
                Reject
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
